import React from 'react';
import { Button, Box, Container, FormField, Input, SpaceBetween, StatusIndicator, Pagination } from '@amzn/awsui-components-core';
import { Employee, AttendeePriority } from '../../interfaces/interface';
import { PRIORITY_COLORS } from '../../constants/EAHuddleConstants';
import { useEmployeeSearch } from '../../hooks/useEmployeeSearch';
import { EmployeeResultsList } from './EmployeeResultsList';
import './EmployeeSearchBar.scss';

interface EmployeeSearchResult extends Employee {
  isAlreadyAdded?: boolean;
}

interface EmployeeSearchBarProps {
  onAddAttendee: (employee: Employee, priority: AttendeePriority) => void;
  existingEmails: Set<string>;
  /** Custom placeholder text (default: "Add attendees to the meeting...") */
  placeholder?: string;
  /** Show single "Add" button instead of "Required"/"Optional" buttons */
  singleAddButton?: boolean;
  /** Custom add button text when singleAddButton is true (default: "Add") */
  addButtonText?: string;
  /** Stretch search bar to full container width (default: false) */
  fullWidth?: boolean;
}

export const EmployeeSearchBar: React.FC<EmployeeSearchBarProps> = ({
  onAddAttendee,
  existingEmails,
  placeholder = 'Add attendees to the meeting...',
  singleAddButton = false,
  addButtonText = 'Add',
  fullWidth = false
}) => {
  // Use the custom hook with standard settings for the main search bar
  const { searchQuery, setSearchQuery, searchResults, isSearching, hasError, clearResults, currentPage, totalCount, hasMore, goToPage } =
    useEmployeeSearch({
      debounceMs: 500, // Reduced from 1000ms for better responsiveness
      minCharacters: 2, // Require at least 2 characters before searching
      maxResults: 15,
      existingEmails
    });

  const handleAddEmployee = (employee: Employee, priority: AttendeePriority = AttendeePriority.Optional) => {
    onAddAttendee(employee, priority);
    setSearchQuery('');
    clearResults();
  };

  // Render action buttons for each employee
  const renderEmployeeActions = (employee: EmployeeSearchResult) => {
    const isAlreadyAdded = employee.isAlreadyAdded;

    return (
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', alignItems: 'center' }}>
        {isAlreadyAdded && <span className="priority-badge added-badge">Already Added</span>}
        {singleAddButton ? (
          <Button
            variant="link"
            disabled={isAlreadyAdded}
            onClick={(e) => {
              e.stopPropagation();
              handleAddEmployee(employee, AttendeePriority.Optional);
            }}
          >
            {addButtonText}
          </Button>
        ) : (
          [AttendeePriority.Critical, AttendeePriority.Required, AttendeePriority.Optional].map((p) => {
            const colors = PRIORITY_COLORS[p];
            const label = p.charAt(0).toUpperCase() + p.slice(1);
            return (
              <button
                key={p}
                type="button"
                disabled={isAlreadyAdded}
                className="priority-badge"
                style={{
                  backgroundColor: colors.background,
                  color: colors.color,
                  cursor: isAlreadyAdded ? 'default' : 'pointer',
                  opacity: isAlreadyAdded ? 0.5 : 1
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAlreadyAdded) handleAddEmployee(employee, p);
                }}
              >
                {label}
              </button>
            );
          })
        )}
      </div>
    );
  };

  const wrapperClassName = fullWidth ? 'employee-search-wrapper employee-search-wrapper--full-width' : 'employee-search-wrapper';

  return (
    <div className={wrapperClassName}>
      <SpaceBetween direction="vertical" size="s">
        {/* Clean Search Bar */}
        <FormField>
          <Input value={searchQuery} onChange={({ detail }) => setSearchQuery(detail.value)} placeholder={placeholder} type="search" />
        </FormField>
      </SpaceBetween>

      {/* Search Results - Outside SpaceBetween to prevent layout expansion */}
      {searchQuery.trim() && (searchResults.length > 0 || isSearching) && (
        <div className="employee-search-results-dropdown">
          <Container disableContentPaddings>
            <SpaceBetween direction="vertical" size="xs">
              {isSearching && (
                <Box textAlign="center" padding="s">
                  <StatusIndicator type="loading">Searching employees...</StatusIndicator>
                </Box>
              )}

              {!isSearching && searchResults.length > 0 && (
                <>
                  <div className="employee-search-results-scroll">
                    <EmployeeResultsList
                      employees={searchResults}
                      onEmployeeSelect={(employee) => handleAddEmployee(employee, AttendeePriority.Optional)}
                      isLoading={false}
                      searchQuery={searchQuery}
                      maxHeight="400px"
                      renderActions={renderEmployeeActions}
                      showDetails={true}
                      getEmployeeDisabled={(employee) => (employee as any).isAlreadyAdded || false}
                    />
                  </div>
                  {/* Pagination footer */}
                  <Pagination
                    currentPageIndex={currentPage}
                    onChange={({ detail }) => goToPage(detail.currentPageIndex)}
                    openEnd={hasMore}
                    pagesCount={hasMore ? currentPage + 1 : currentPage}
                  />
                </>
              )}

              {!isSearching && searchResults.length === 0 && searchQuery.trim() && (
                <Box textAlign="center" color="text-status-info" padding="s">
                  No employees found matching "{searchQuery}". Try a different search term.
                </Box>
              )}
            </SpaceBetween>
          </Container>
        </div>
      )}
    </div>
  );
};
